import { t } from '@lingui/macro'
import CloseIcon from '@mui/icons-material/Close'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import { CandidateDTO, CandidatePatchDTO, CarePartnerResponseDTO, CarePartnerUpdateDTO } from '@om1/aspen-api'
import React, { useEffect, useState } from 'react'

interface EditContactInfoModalProps {
    open: boolean
    onClose: () => void
    onSave: (data: CandidatePatchDTO | CarePartnerUpdateDTO) => Promise<void>
    candidate?: CandidateDTO
    carePartner?: CarePartnerResponseDTO
    loading?: boolean
}

interface FormData {
    phone: string
    email: string
    dateOfBirth: string
    smsConsent: boolean
    additionalPhone1: string
    additionalPhone1Label: string
    additionalPhone2: string
    additionalPhone2Label: string
}

interface ValidationErrors {
    phone?: string
    email?: string
    dateOfBirth?: string
    additionalPhone1?: string
    additionalPhone1Label?: string
    additionalPhone2?: string
    additionalPhone2Label?: string
}

const EditContactInfoModal: React.FC<EditContactInfoModalProps> = ({ open, onClose, onSave, candidate, carePartner, loading = false }) => {
    const entity = candidate || carePartner
    const getPhone = () => {
        if (candidate) return candidate.phone || ''
        if (carePartner) return carePartner.phoneNumber || ''
        return ''
    }
    const [formData, setFormData] = useState<FormData>({
        phone: getPhone(),
        email: entity?.email || '',
        dateOfBirth: entity?.dateOfBirth ? (typeof entity.dateOfBirth === 'string' ? entity.dateOfBirth.split('T')[0] : entity.dateOfBirth) : '', // Convert to YYYY-MM-DD format
        smsConsent: entity?.smsConsent || false,
        additionalPhone1: (entity?.additionalPhone1 as string) || '',
        additionalPhone1Label: (entity?.additionalPhone1Label as string) || '',
        additionalPhone2: (entity?.additionalPhone2 as string) || '',
        additionalPhone2Label: (entity?.additionalPhone2Label as string) || ''
    })

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [hasChanges, setHasChanges] = useState(false)

    // Reset form when modal opens/closes or entity changes
    useEffect(() => {
        if (open && entity) {
            // Format phone number to match the expected format
            const phoneValue = candidate?.phone || carePartner?.phoneNumber || ''
            let formattedPhone = phoneValue
            if (formattedPhone && !formattedPhone.includes('-')) {
                // Convert unformatted phone to formatted
                const digits = formattedPhone.replace(/\D/g, '')
                if (digits.length === 10) {
                    formattedPhone = digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
                }
            }

            const dateOfBirthValue = entity.dateOfBirth
            const dateOfBirthString = dateOfBirthValue
                ? typeof dateOfBirthValue === 'string'
                    ? dateOfBirthValue.split('T')[0]
                    : dateOfBirthValue
                : ''

            setFormData({
                phone: formattedPhone,
                email: entity.email || '',
                dateOfBirth: dateOfBirthString,
                smsConsent: entity.smsConsent || false,
                additionalPhone1: entity.additionalPhone1 || '',
                additionalPhone1Label: entity.additionalPhone1Label || '',
                additionalPhone2: entity.additionalPhone2 || '',
                additionalPhone2Label: entity.additionalPhone2Label || ''
            })
            setErrors({})
            setHasChanges(false)
        }
    }, [open, candidate, carePartner, entity])

    // Check for changes whenever form data changes
    useEffect(() => {
        if (!entity) return
        const phoneValue = candidate?.phone || carePartner?.phoneNumber || ''
        const dateOfBirthValue = entity.dateOfBirth
        const dateOfBirthString = dateOfBirthValue ? (typeof dateOfBirthValue === 'string' ? dateOfBirthValue.split('T')[0] : dateOfBirthValue) : ''
        const originalData = {
            phone: phoneValue,
            email: entity.email || '',
            dateOfBirth: dateOfBirthString,
            smsConsent: entity.smsConsent || false,
            additionalPhone1: entity.additionalPhone1 || '',
            additionalPhone1Label: entity.additionalPhone1Label || '',
            additionalPhone2: entity.additionalPhone2 || '',
            additionalPhone2Label: entity.additionalPhone2Label || ''
        }

        const hasFormChanges =
            formData.phone !== originalData.phone ||
            formData.email !== originalData.email ||
            formData.dateOfBirth !== originalData.dateOfBirth ||
            formData.smsConsent !== originalData.smsConsent ||
            formData.additionalPhone1 !== originalData.additionalPhone1 ||
            formData.additionalPhone1Label !== originalData.additionalPhone1Label ||
            formData.additionalPhone2 !== originalData.additionalPhone2 ||
            formData.additionalPhone2Label !== originalData.additionalPhone2Label

        setHasChanges(hasFormChanges)
    }, [formData, candidate, carePartner, entity])

    const validatePhone = (phone: string): string | undefined => {
        if (!phone) return t`Phone number is required`
        // Use the same validation pattern as the original form: XXX-XXX-XXXX
        const phoneRegex = /^\d{3}-\d{3}-\d{4}$/
        if (!phoneRegex.test(phone)) {
            return t`Format 123-456-7890`
        }
        return undefined
    }

    const validateEmail = (email: string): string | undefined => {
        if (!email) return t`Email is required`
        // Use the same email validation as the original form (Yup's email validation)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return t`Invalid email address`
        }
        if (email.length > 256) {
            return t`Email must be 256 characters or less`
        }
        return undefined
    }

    const validateDateOfBirth = (dateOfBirth: string): string | undefined => {
        if (!dateOfBirth) return t`Date of birth is required`
        // Use the same date validation as the original form
        const date = new Date(dateOfBirth)
        if (isNaN(date.getTime())) {
            return t`Invalid date`
        }
        const today = new Date()
        if (date > today) {
            return t`Date of birth cannot be in the future`
        }
        if (date < new Date('1900-01-01')) {
            return t`Please enter a valid date of birth`
        }
        return undefined
    }

    const validateAdditionalPhone = (phone: string, label: string): { phone?: string; label?: string } => {
        const errors: { phone?: string; label?: string } = {}

        const hasPhone = phone.trim().length > 0
        const hasLabel = label.trim().length > 0

        // If both are empty, that's fine (deleting the entry)
        if (!hasPhone && !hasLabel) {
            return errors
        }

        // If phone exists, label is required
        if (hasPhone && !hasLabel) {
            errors.label = t`Label is required when phone number is provided`
        }

        // If label exists, phone is required
        if (hasLabel && !hasPhone) {
            errors.phone = t`Phone number is required when label is provided`
        }

        // Validate phone format if phone exists
        if (hasPhone) {
            // Check pattern: only digits, spaces, dashes, plus signs, parentheses
            if (!/^[0-9\s\-+()]+$/.test(phone)) {
                errors.phone = t`Phone number can only contain digits, spaces, dashes, plus signs, and parentheses`
            }
            // Check that at least one digit is present
            else if (!/[0-9]/.test(phone)) {
                errors.phone = t`Phone number must contain at least one digit`
            }
            // Check max length
            else if (phone.length > 20) {
                errors.phone = t`Phone number must be 20 characters or less`
            }
        }

        // Validate label format if label exists
        if (hasLabel && label.trim().length > 50) {
            errors.label = t`Label must be 50 characters or less`
        }

        return errors
    }

    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {}

        const phoneError = validatePhone(formData.phone)
        if (phoneError) newErrors.phone = phoneError

        const emailError = validateEmail(formData.email)
        if (emailError) newErrors.email = emailError

        const dateError = validateDateOfBirth(formData.dateOfBirth)
        if (dateError) newErrors.dateOfBirth = dateError

        // Validate additional phones if they exist
        if (entity?.additionalPhone1) {
            const phone1Errors = validateAdditionalPhone(formData.additionalPhone1, formData.additionalPhone1Label)
            if (phone1Errors.phone) newErrors.additionalPhone1 = phone1Errors.phone
            if (phone1Errors.label) newErrors.additionalPhone1Label = phone1Errors.label
        }
        if (entity?.additionalPhone2) {
            const phone2Errors = validateAdditionalPhone(formData.additionalPhone2, formData.additionalPhone2Label)
            if (phone2Errors.phone) newErrors.additionalPhone2 = phone2Errors.phone
            if (phone2Errors.label) newErrors.additionalPhone2Label = phone2Errors.label
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleInputChange = (field: keyof FormData, value: string | boolean) => {
        setFormData((prev) => {
            const newFormData = { ...prev, [field]: value }

            // Validate additional phone fields in real-time
            if (field === 'additionalPhone1' || field === 'additionalPhone1Label') {
                const phone1Errors = validateAdditionalPhone(newFormData.additionalPhone1, newFormData.additionalPhone1Label)
                setErrors((prev) => ({
                    ...prev,
                    additionalPhone1: phone1Errors.phone,
                    additionalPhone1Label: phone1Errors.label
                }))
            } else if (field === 'additionalPhone2' || field === 'additionalPhone2Label') {
                const phone2Errors = validateAdditionalPhone(newFormData.additionalPhone2, newFormData.additionalPhone2Label)
                setErrors((prev) => ({
                    ...prev,
                    additionalPhone2: phone2Errors.phone,
                    additionalPhone2Label: phone2Errors.label
                }))
            } else {
                // Clear error for other fields when user starts typing
                if (errors[field as keyof ValidationErrors]) {
                    setErrors((prev) => ({ ...prev, [field]: undefined }))
                }
            }

            return newFormData
        })
    }

    const handlePhoneChange = (value: string) => {
        // Use the same phone formatting as the original form
        let digits = value.replace(/\D/g, '')

        if (digits.length > 10) {
            digits = digits.substring(0, 10)
        }

        let formattedPhone = ''
        if (digits.length === 10) {
            formattedPhone = digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
        } else if (digits.length > 6) {
            formattedPhone = digits.replace(/(\d{3})(\d{3})/, '$1-$2-')
        } else if (digits.length > 3) {
            formattedPhone = digits.replace(/(\d{3})/, '$1-')
        } else {
            formattedPhone = digits
        }

        handleInputChange('phone', formattedPhone)
    }

    const formatPhoneDisplay = (phone: string) => {
        // Return the phone as-is since it's already formatted
        return phone
    }

    const handleSave = async () => {
        if (!validateForm()) return

        // Only include additional phone fields if both phone and label are provided
        // If either is empty, set both to null to delete the entry
        if (candidate) {
            const patchData: CandidatePatchDTO = {
                id: candidate.id,
                phone: formData.phone || null, // Phone is already in XXX-XXX-XXXX format
                email: formData.email,
                dateOfBirth: formData.dateOfBirth,
                smsConsent: formData.smsConsent,
                additionalPhone1: formData.additionalPhone1.trim() && formData.additionalPhone1Label.trim() ? formData.additionalPhone1.trim() : null,
                additionalPhone1Label:
                    formData.additionalPhone1.trim() && formData.additionalPhone1Label.trim() ? formData.additionalPhone1Label.trim() : null,
                additionalPhone2: formData.additionalPhone2.trim() && formData.additionalPhone2Label.trim() ? formData.additionalPhone2.trim() : null,
                additionalPhone2Label:
                    formData.additionalPhone2.trim() && formData.additionalPhone2Label.trim() ? formData.additionalPhone2Label.trim() : null
            }

            try {
                await onSave(patchData)
                onClose()
            } catch (error) {
                // Error handling is done in the parent component
                console.error(t`Error saving contact information:`, error)
            }
        } else if (carePartner) {
            const patchData: CarePartnerUpdateDTO = {
                phoneNumber: formData.phone || null, // Phone is already in XXX-XXX-XXXX format
                email: formData.email,
                dateOfBirth: formData.dateOfBirth,
                smsConsent: formData.smsConsent,
                additionalPhone1: formData.additionalPhone1.trim() && formData.additionalPhone1Label.trim() ? formData.additionalPhone1.trim() : null,
                additionalPhone1Label:
                    formData.additionalPhone1.trim() && formData.additionalPhone1Label.trim() ? formData.additionalPhone1Label.trim() : null,
                additionalPhone2: formData.additionalPhone2.trim() && formData.additionalPhone2Label.trim() ? formData.additionalPhone2.trim() : null,
                additionalPhone2Label:
                    formData.additionalPhone2.trim() && formData.additionalPhone2Label.trim() ? formData.additionalPhone2Label.trim() : null
            }

            try {
                await onSave(patchData)
                onClose()
            } catch (error) {
                // Error handling is done in the parent component
                console.error(t`Error saving contact information:`, error)
            }
        }
    }

    const handleCancel = () => {
        if (!entity) return
        // Reset form to original values
        const phoneValue = candidate ? candidate.phone : carePartner?.phoneNumber || ''
        const dateOfBirthValue = entity.dateOfBirth
        const dateOfBirthString = dateOfBirthValue ? (typeof dateOfBirthValue === 'string' ? dateOfBirthValue.split('T')[0] : dateOfBirthValue) : ''
        setFormData({
            phone: phoneValue || '',
            email: entity.email || '',
            dateOfBirth: dateOfBirthString,
            smsConsent: entity.smsConsent || false,
            additionalPhone1: (entity.additionalPhone1 as string) || '',
            additionalPhone1Label: (entity.additionalPhone1Label as string) || '',
            additionalPhone2: (entity.additionalPhone2 as string) || '',
            additionalPhone2Label: (entity.additionalPhone2Label as string) || ''
        })
        setErrors({})
        onClose()
    }

    const isValid =
        !Object.values(errors).some((error) => error && error.trim() !== '') &&
        formData.phone.trim() &&
        formData.email.trim() &&
        formData.dateOfBirth.trim()

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='h6' component='div'>
                    {t`Edit Contact Information`}
                </Typography>
                <IconButton
                    aria-label='close'
                    onClick={handleCancel}
                    disabled={loading}
                    sx={{
                        color: (theme) => theme.palette.grey[500]
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {loading && <LinearProgress />}

            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSave()
                }}
            >
                <DialogContent dividers sx={{ pt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {!hasChanges && (
                            <Alert severity='info' sx={{ mb: 2 }}>
                                {t`No changes detected. Make changes to enable the Save button.`}
                            </Alert>
                        )}

                        <TextField
                            label={t`Phone Number *`}
                            placeholder={t`123-456-7890`}
                            fullWidth
                            value={formatPhoneDisplay(formData.phone)}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone || ''}
                            disabled={loading}
                            inputProps={{ maxLength: 12 }}
                        />

                        <TextField
                            label={t`Email Address *`}
                            placeholder={t`Enter email address`}
                            type='email'
                            fullWidth
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email || ''}
                            disabled={loading}
                            inputProps={{ maxLength: 256 }}
                        />

                        <TextField
                            label={t`Date of Birth *`}
                            type='date'
                            fullWidth
                            value={formData.dateOfBirth}
                            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                            error={!!errors.dateOfBirth}
                            helperText={errors.dateOfBirth || ''}
                            disabled={loading}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                max: new Date().toISOString().split('T')[0] // Disable future dates
                            }}
                        />

                        <FormControl component='fieldset'>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.smsConsent}
                                        onChange={(e) => handleInputChange('smsConsent', e.target.checked)}
                                        disabled={loading}
                                        color='primary'
                                    />
                                }
                                label={t`SMS Consent`}
                            />
                        </FormControl>

                        {/* Additional Phone Numbers - only show if they exist */}
                        {((entity?.additionalPhone1 as string) || (entity?.additionalPhone2 as string)) && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 2 }}>
                                    {t`Additional Phone Numbers`}
                                </Typography>

                                {(entity?.additionalPhone1 as string) && (
                                    <Box sx={{ mb: 4 }}>
                                        <TextField
                                            label={t`Label`}
                                            placeholder={t`e.g., Work, Home, Emergency`}
                                            fullWidth
                                            value={formData.additionalPhone1Label}
                                            onChange={(e) => handleInputChange('additionalPhone1Label', e.target.value)}
                                            onBlur={() => {
                                                const phone1Errors = validateAdditionalPhone(
                                                    formData.additionalPhone1,
                                                    formData.additionalPhone1Label
                                                )
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    additionalPhone1: phone1Errors.phone,
                                                    additionalPhone1Label: phone1Errors.label
                                                }))
                                            }}
                                            error={!!errors.additionalPhone1Label}
                                            helperText={errors.additionalPhone1Label || ''}
                                            disabled={loading}
                                            inputProps={{ maxLength: 50 }}
                                            sx={{ mb: 3 }}
                                        />
                                        <TextField
                                            label={t`Phone Number`}
                                            placeholder={t`e.g., +1 638-383-9939 or (123) 456-7890`}
                                            fullWidth
                                            value={formData.additionalPhone1}
                                            onChange={(e) => handleInputChange('additionalPhone1', e.target.value)}
                                            onBlur={() => {
                                                const phone1Errors = validateAdditionalPhone(
                                                    formData.additionalPhone1,
                                                    formData.additionalPhone1Label
                                                )
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    additionalPhone1: phone1Errors.phone,
                                                    additionalPhone1Label: phone1Errors.label
                                                }))
                                            }}
                                            error={!!errors.additionalPhone1}
                                            helperText={
                                                errors.additionalPhone1 ||
                                                t`Digits, spaces, dashes, plus signs, and parentheses allowed. Max 20 characters.`
                                            }
                                            disabled={loading}
                                            inputProps={{ maxLength: 20 }}
                                        />
                                    </Box>
                                )}

                                {(entity?.additionalPhone2 as string) && (
                                    <Box>
                                        <TextField
                                            label={t`Label`}
                                            placeholder={t`e.g., Work, Home, Emergency`}
                                            fullWidth
                                            value={formData.additionalPhone2Label}
                                            onChange={(e) => handleInputChange('additionalPhone2Label', e.target.value)}
                                            onBlur={() => {
                                                const phone2Errors = validateAdditionalPhone(
                                                    formData.additionalPhone2,
                                                    formData.additionalPhone2Label
                                                )
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    additionalPhone2: phone2Errors.phone,
                                                    additionalPhone2Label: phone2Errors.label
                                                }))
                                            }}
                                            error={!!errors.additionalPhone2Label}
                                            helperText={errors.additionalPhone2Label || ''}
                                            disabled={loading}
                                            inputProps={{ maxLength: 50 }}
                                            sx={{ mb: 3 }}
                                        />
                                        <TextField
                                            label={t`Phone Number`}
                                            placeholder={t`e.g., +1 638-383-9939 or (123) 456-7890`}
                                            fullWidth
                                            value={formData.additionalPhone2}
                                            onChange={(e) => handleInputChange('additionalPhone2', e.target.value)}
                                            onBlur={() => {
                                                const phone2Errors = validateAdditionalPhone(
                                                    formData.additionalPhone2,
                                                    formData.additionalPhone2Label
                                                )
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    additionalPhone2: phone2Errors.phone,
                                                    additionalPhone2Label: phone2Errors.label
                                                }))
                                            }}
                                            error={!!errors.additionalPhone2}
                                            helperText={
                                                errors.additionalPhone2 ||
                                                t`Digits, spaces, dashes, plus signs, and parentheses allowed. Max 20 characters.`
                                            }
                                            disabled={loading}
                                            inputProps={{ maxLength: 20 }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleCancel} disabled={loading}>
                        {t`Cancel`}
                    </Button>
                    <Button type='submit' variant='contained' disabled={loading || !hasChanges || !isValid} sx={{ minWidth: 120, height: 36 }}>
                        {loading ? t`Saving...` : t`Save Changes`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default EditContactInfoModal
